import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Phone, Clock, CheckCircle2, X, AlarmClockCheck } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * StrongNotificationPopup
 * Polls /api/notifications for unread "lead_reschedule_due" notifications
 * and shows them as a persistent toast in the bottom-right corner.
 * The user can: open the lead, snooze 5/15/30 min, or mark as read.
 */
export const StrongNotificationPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState([]); // unread strong notifs
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const on = () => setDragging(true);
    const off = () => setDragging(false);
    document.addEventListener('dragstart', on);
    document.addEventListener('dragend', off);
    document.addEventListener('drop', off);
    return () => { document.removeEventListener('dragstart', on); document.removeEventListener('dragend', off); document.removeEventListener('drop', off); };
  }, []);

  const fetchActive = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/notifications`);
      const items = Array.isArray(res.data) ? res.data : (res.data?.notifications || []);
      const strong = items.filter(n =>
        !n.read && ['lead_reschedule_due', 'lead_claim_request', 'lead_pool_abandoned', 'calendar_event_reminder'].includes(n.type)
      );
      setActive(strong);
    } catch {
      /* silent */
    }
  }, [user]);

  useEffect(() => {
    fetchActive();
    const interval = setInterval(fetchActive, 30000);
    return () => clearInterval(interval);
  }, [fetchActive]);

  const markRead = async (notifId) => {
    try {
      await axios.put(`${API_URL}/notifications/${notifId}/read`);
      setActive(prev => prev.filter(n => n.id !== notifId));
    } catch {
      /* silent */
    }
  };

  const open = (notif) => {
    if (notif.meta?.lead_id) {
      navigate(`/leads/${notif.meta.lead_id}`);
    } else if (notif.link) {
      navigate(notif.link);
    }
    markRead(notif.id);
  };

  const snooze = async (notif, minutes) => {
    if (!notif.meta?.lead_id) {
      markRead(notif.id);
      return;
    }
    try {
      const next = new Date(Date.now() + minutes * 60 * 1000).toISOString();
      await axios.post(`${API_URL}/leads/${notif.meta.lead_id}/reschedule`, {
        next_action_at: next,
        notes: `Adiado ${minutes} min`,
      });
      await markRead(notif.id);
    } catch {
      /* silent */
    }
  };

  if (active.length === 0) return null;

  // Show only the most recent 3 to avoid overwhelming
  const visible = active.slice(0, 3);

  return (
    <div className={`fixed bottom-4 right-4 z-[60] flex flex-col gap-3 max-w-sm w-full transition-opacity duration-150 ${dragging ? 'opacity-0 pointer-events-none' : ''}`} data-testid="strong-notif-popup">
      {visible.map(notif => (
        <div
          key={notif.id}
          className="bg-white border-2 border-rose-300 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-right duration-300"
          data-testid={`strong-notif-${notif.id}`}
        >
          {/* Pulsing red header */}
          <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-4 py-2 flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-200 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
            </span>
            <span className="text-xs font-bold uppercase tracking-wide text-white flex-1 truncate">
              {notif.title}
            </span>
            <button
              type="button"
              onClick={() => markRead(notif.id)}
              className="text-white/80 hover:text-white"
              title="Marcar como lida"
              data-testid={`dismiss-${notif.id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          
          <div className="p-4 space-y-3">
            <p className="text-sm text-slate-700">{notif.message}</p>
            
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => open(notif)}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-1.5 text-xs h-8 flex-1"
                data-testid={`open-${notif.id}`}
              >
                {notif.type === 'lead_claim_request' ? (
                  <>Ver e Aceitar</>
                ) : notif.type === 'lead_pool_abandoned' ? (
                  <>Ver Lead</>
                ) : notif.type === 'calendar_event_reminder' ? (
                  <>Ver Evento</>
                ) : (
                  <><Phone className="w-3.5 h-3.5" /> Ligar agora</>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => markRead(notif.id)}
                className="rounded-full gap-1.5 text-xs h-8"
                data-testid={`mark-read-${notif.id}`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Lida
              </Button>
            </div>
            
            {notif.type === 'lead_reschedule_due' && (
              <div className="flex items-center gap-1 pt-1">
                <span className="text-[10px] text-slate-400 mr-1">Adiar:</span>
                {[5, 15, 30].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => snooze(notif, m)}
                    className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                    data-testid={`snooze-${m}-${notif.id}`}
                  >
                    +{m}m
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      
      {active.length > visible.length && (
        <p className="text-xs text-slate-500 text-center bg-white/90 backdrop-blur rounded-full py-1.5 px-3 shadow border border-slate-100">
          + {active.length - visible.length} reagendamento{active.length - visible.length === 1 ? '' : 's'} pendente{active.length - visible.length === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
};

export default StrongNotificationPopup;
